<template>
  <!-- Q3 (PM lite) "Pace" from the Work Progress design: progress gained per week over the last
       N weeks, tasks completed per week, and how long the remainder takes at that pace -->
  <v-card class="pace-card">
    <div class="head">
      <h2 class="card-title">{{ $t('taskPace.title') }}</h2>
      <v-btn-toggle v-model="weeks" mandatory density="compact" color="info" variant="tonal" class="seg">
        <v-btn v-for="n in [4, 6, 8]" :key="n" :value="n" size="small" class="seg-btn">
          {{ $t('taskWorkload.weekBtn', { n }) }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-skeleton-loader v-if="!rows.length && loading" type="image" height="150" />

    <template v-else>
      <div class="kpis">
        <div class="kpi" style="border-left-color: #4CAF50">
          <span class="kpi-value tk-ok">{{ signedPct(stats.rate) }}</span>
          <span class="kpi-label">{{ $t('taskPace.progressPerWeek') }}</span>
        </div>
        <div class="kpi" style="border-left-color: #2196F3">
          <span class="kpi-value">{{ stats.tasksPerWeek.toFixed(1) }}</span>
          <span class="kpi-label">{{ $t('taskPace.tasksPerWeek') }}</span>
        </div>
        <div class="kpi" :style="{ borderLeftColor: stats.etaWeeks === null ? '#5C6B7A' : (stats.etaWeeks > 26 ? '#F5A623' : '#4CAF50') }">
          <span class="kpi-value" :class="stats.etaWeeks === null ? '' : (stats.etaWeeks > 26 ? 'tk-warn' : 'tk-ok')">
            {{ stats.etaWeeks === null ? '–' : $t('taskPace.weeksShort', { n: stats.etaWeeks.toLocaleString() }) }}
          </span>
          <span class="kpi-label">{{ $t('taskPace.toFinish') }}</span>
        </div>
      </div>

      <svg viewBox="0 0 600 140" width="100%" role="img" class="chart">
        <line x1="6" y1="112" x2="594" y2="112" class="axis" />
        <line x1="6" :y1="avgY" x2="594" :y2="avgY" stroke="#8BE09A" stroke-width="1" stroke-dasharray="4 3" opacity=".8" />
        <text x="594" :y="avgY" dy="-4" font-size="10" text-anchor="end" fill="#8BE09A">{{ $t('taskPace.avg') }} {{ signedPct(stats.rate) }}</text>
        <g v-for="bar in bars" :key="bar.key">
          <rect :x="bar.x" :y="bar.y" :width="bar.w" :height="bar.h" rx="2" :fill="bar.fill" />
          <text :x="bar.cx" :y="bar.ty" font-size="11" font-weight="500" text-anchor="middle" class="bar-n">{{ bar.v }}</text>
          <text :x="bar.cx" y="130" font-size="10" text-anchor="middle" class="bar-label">{{ bar.label }}</text>
        </g>
      </svg>

      <div class="verdict" :class="verdict.cls">{{ verdict.text }}</div>
    </template>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { paceSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady } from '@/utils/taskSCurveFilters';

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);

const weeks = ref(8);
const rows = ref([]);
const loading = ref(false);

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    const result = await queryRows(paceSql({ ...taskScope(), weeks: weeks.value }));
    if (version !== queryVersion) return; // a newer request superseded this one
    rows.value = result;
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskPaceChart] query failed:', error.message);
    rows.value = [];
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

// Week k's gain = % at week k − % at week k+1 (k = 0 is the week ending today)
const weekly = computed(() => {
  const byK = new Map(rows.value.map(r => [Number(r.k), { pct: Number(r.progressPct ?? 0), completed: Number(r.completed ?? 0) }]));
  const out = [];
  for (let k = 0; k < weeks.value * 2; k++) {
    const cur = byK.get(k)?.pct ?? 0;
    const prev = byK.get(k + 1)?.pct ?? 0;
    out.push({ k, gain: Math.max(0, cur - prev), completed: byK.get(k)?.completed ?? 0 });
  }
  return out;
});

const avg = list => (list.length ? list.reduce((s, v) => s + v, 0) / list.length : 0);

const stats = computed(() => {
  const n = weeks.value;
  const window = weekly.value.slice(0, n);
  const previous = weekly.value.slice(n, n * 2);
  const rate = avg(window.map(w => w.gain));
  const prevRate = avg(previous.map(w => w.gain));
  const current = Number(rows.value.find(r => Number(r.k) === 0)?.progressPct ?? 0);
  const remaining = Math.max(0, 100 - current);
  return {
    rate,
    prevRate,
    tasksPerWeek: avg(window.map(w => w.completed)),
    etaWeeks: rate > 0 && remaining > 0 ? Math.ceil(remaining / rate) : (remaining === 0 ? 0 : null),
  };
});

const signedPct = v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

// Bars oldest → newest across x 6–594, baseline y 112, up to 84px tall
const bars = computed(() => {
  if (!rows.value.length) return [];
  const window = weekly.value.slice(0, weeks.value).reverse();
  const max = Math.max(0.1, ...window.map(w => w.gain));
  const slotW = 588 / window.length;
  const barW = Math.min(44, slotW * 0.62);
  return window.map((w, i) => {
    const cx = 6 + i * slotW + slotW / 2;
    const h = w.gain / max * 84;
    const y = 112 - h;
    return {
      key: w.k,
      x: (cx - barW / 2).toFixed(1),
      y: y.toFixed(1),
      w: barW.toFixed(1),
      h: Math.max(h, 1.5).toFixed(1),
      cx: cx.toFixed(1),
      ty: (y - 4).toFixed(1),
      v: signedPct(w.gain),
      label: w.k === 0 ? t('taskSCurve.today') : t('taskWorkload.wkPast', { n: w.k }),
      fill: '#4CAF50',
    };
  });
});

const avgY = computed(() => {
  const window = weekly.value.slice(0, weeks.value);
  const max = Math.max(0.1, ...window.map(w => w.gain));
  return (112 - stats.value.rate / max * 84).toFixed(1);
});

// Compared with the same number of weeks before the window: ±10% counts as steady
const verdict = computed(() => {
  const { rate, prevRate } = stats.value;
  const n = weeks.value;
  if (prevRate <= 0 && rate > 0) return { text: t('taskPace.verdictUp', { n }), cls: 'tk-ok' };
  if (prevRate <= 0 && rate <= 0) return { text: t('taskPace.verdictSteady', { n }), cls: 'tk-ok' };
  const ratio = rate / prevRate;
  if (ratio > 1.1) return { text: t('taskPace.verdictUp', { n }), cls: 'tk-ok' };
  if (ratio < 0.9) return { text: t('taskPace.verdictDown', { n }), cls: 'tk-warn' };
  return { text: t('taskPace.verdictSteady', { n }), cls: 'tk-ok' };
});

onMounted(loadData);
watch(
  [dataLoaded, weeks, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.pace-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 14px;
}
.head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.seg {
  height: 30px;
}
.seg-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.kpi {
  display: flex;
  flex-direction: column;
  border-left: 4px solid;
  padding-left: 10px;
  min-width: 0;
}
.kpi-value {
  font-size: 26px;
  line-height: 30px;
  font-variant-numeric: tabular-nums;
}
.kpi-label {
  font-size: 11px;
  opacity: 0.65;
}
.chart {
  display: block;
  flex: 1 1 auto;
}
.axis {
  stroke: currentColor;
  stroke-opacity: 0.2;
  stroke-width: 1;
}
.bar-n {
  fill: currentColor;
}
.bar-label {
  fill: currentColor;
  fill-opacity: 0.6;
}
.verdict {
  font-size: 12px;
}
</style>
